import RequestDescription from "./RequestDescription";
import { downloadFileName } from "./TemplateRequestHistory.support";
import {
  CloseRounded as CloseIcon,
  DescriptionRounded as DocIcon,
  DownloadRounded as DownloadIcon,
  PlaceRounded as PlaceIcon,
  BorderColorRounded as AnnotationIcon,
  RadioButtonUncheckedRounded as CircleIcon,
  HorizontalRuleRounded as LineIcon,
  ArrowRightAltRounded as ArrowIcon,
  CropSquareRounded as BoxIcon,
  HighlightRounded as HighlightIcon,
  ChatBubbleOutlineRounded as CommentIcon,
  TextFieldsRounded as TextIcon,
  NumbersRounded as NumberIcon,
  CalendarMonthRounded as DateIcon,
  ZoomInRounded as ZoomInIcon,
  ZoomOutRounded as ZoomOutIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { renderAsync } from "docx-preview";
import { getDownloadUrl, saveFile } from "fileUtils";
import { useState, useEffect, useRef, useCallback } from "react";

const ANNOTATION_TOOLS = [
  { id: "select", label: "Select", color: "#666666" },
  { id: "circle", icon: CircleIcon, label: "Circle", color: "#e53935" },
  { id: "line", icon: LineIcon, label: "Line", color: "#1e88e5" },
  { id: "arrow", icon: ArrowIcon, label: "Arrow", color: "#43a047" },
  { id: "box", icon: BoxIcon, label: "Rectangle", color: "#fb8c00" },
  {
    id: "highlight",
    icon: HighlightIcon,
    label: "Highlight",
    color: "#fdd835",
  },
  { id: "comment", icon: CommentIcon, label: "Comment", color: "#8e24aa" },
];

const getTypeIcon = (type) => {
  switch (type) {
    case "string":
      return <TextIcon fontSize={"inherit"} />;
    case "number":
      return <NumberIcon fontSize={"inherit"} />;
    case "date":
      return <DateIcon fontSize={"inherit"} />;
    default:
      return <TextIcon fontSize={"inherit"} />;
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case "string":
      return "#1976d2";
    case "number":
      return "#2e7d32";
    case "date":
      return "#ed6c02";
    default:
      return "#757575";
  }
};

const PreviewAnnotation = ({ annotation, zoom }) => {
  const theme = useTheme();
  const tool = ANNOTATION_TOOLS.find((t) => t.id === annotation.type);
  const color = tool?.color || "#666";

  const baseSx = {
    position: "absolute",
    left: `${annotation.x}%`,
    top: `${annotation.y}%`,
  };

  switch (annotation.type) {
    case "circle":
      return (
        <Box
          sx={{
            ...baseSx,
            transform: "translate(-50%, -50%)",
            width: annotation.width || 60,
            height: annotation.height || 60,
            border: `3px solid ${color}`,
            borderRadius: "50%",
            backgroundColor: `${color}10`,
          }}
        />
      );
    case "box":
      return (
        <Box
          sx={{
            ...baseSx,
            transform: "translate(-50%, -50%)",
            width: annotation.width || 80,
            height: annotation.height || 50,
            border: `3px solid ${color}`,
            backgroundColor: `${color}10`,
          }}
        />
      );
    case "line":
      return (
        <Box
          sx={{
            ...baseSx,
            width: 80,
            height: 3,
            backgroundColor: color,
          }}
        />
      );
    case "arrow":
      return (
        <Box
          sx={{
            ...baseSx,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 3,
              backgroundColor: color,
            }}
          />
          <Box
            sx={{
              width: 0,
              height: 0,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderLeft: `12px solid ${color}`,
              marginLeft: "-2px",
            }}
          />
        </Box>
      );
    case "highlight":
      return (
        <Box
          sx={{
            ...baseSx,
            transform: "translate(-50%, -50%)",
            width: annotation.width || 100,
            height: annotation.height || 24,
            backgroundColor: `${color}60`,
          }}
        />
      );
    case "comment":
      return (
        <Tooltip title={annotation.text || "Comment"} arrow placement={"top"}>
          <Paper
            elevation={3}
            sx={{
              ...baseSx,
              transform: "translate(-50%, -50%)",
              p: 1,
              backgroundColor: color,
              color: theme.brand.white,
              borderRadius: 2,
              maxWidth: 200,
            }}
          >
            <Stack direction={"row"} alignItems={"flex-start"} gap={0.5}>
              <CommentIcon sx={{ fontSize: 16 }} />
              <Typography
                variant={"caption"}
                sx={{
                  fontSize: `${0.7 * (1 / zoom)}rem`,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {annotation.text || "Comment"}
              </Typography>
            </Stack>
          </Paper>
        </Tooltip>
      );
    default:
      return null;
  }
};

const PreviewVariableMarker = ({ placement, zoom }) => {
  const hasCondition = Boolean(placement.condition);
  const typeColor = hasCondition
    ? "#0288d1"
    : getTypeColor(placement.variable.type);

  return (
    <Tooltip
      title={
        hasCondition ? (
          <Box>
            <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
              {"Show if:"}
            </Typography>
            <Typography
              variant={"caption"}
              display={"block"}
              sx={{ fontFamily: "monospace" }}
            >
              {placement.condition}
            </Typography>
          </Box>
        ) : (
          ""
        )
      }
      arrow
    >
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          border: `2px solid ${typeColor}`,
          backgroundColor: hasCondition ? `${typeColor}10` : "white",
          whiteSpace: "nowrap",
          fontSize: `${0.75 * (1 / zoom)}rem`,
        }}
      >
        {getTypeIcon(placement.variable.type)}
        <Typography
          variant={"caption"}
          sx={{
            fontWeight: 500,
            color: typeColor,
            fontSize: "inherit",
          }}
        >
          {placement.variable.label}
        </Typography>
        {hasCondition && (
          <Typography
            variant={"caption"}
            sx={{
              fontWeight: 400,
              color: typeColor,
              fontSize: "inherit",
              fontStyle: "italic",
              opacity: 0.8,
            }}
          >
            {"(if)"}
          </Typography>
        )}
      </Paper>
    </Tooltip>
  );
};

const PreviewCanvas = ({
  placements = [],
  annotations = [],
  zoom,
  s3FileId,
  fileType,
  impersonateCompanyId,
}) => {
  const theme = useTheme();
  const docContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRendered, setIsRendered] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  // Set when the document turns out to be a PDF (by declared type OR by
  // sniffing the bytes) — an object URL forced to application/pdf so it renders
  // inline regardless of how the bucket served the content-type.
  const [pdfUrl, setPdfUrl] = useState(null);

  const isPdf = fileType === "application/pdf";

  const fetchDownloadUrl = useCallback(async () => {
    if (!s3FileId) {
      setError("No document file was attached to this request.");
      return;
    }
    setIsLoading(true);
    try {
      const url = await getDownloadUrl(s3FileId, impersonateCompanyId);
      if (!url) {
        throw new Error("No download URL returned");
      }
      setDownloadUrl(url);
      setError(null);
    } catch {
      setError(
        `Unable to load document preview. The file may no longer be available.`
      );
    } finally {
      setIsLoading(false);
    }
  }, [s3FileId, impersonateCompanyId]);

  useEffect(() => {
    fetchDownloadUrl();
  }, [fetchDownloadUrl]);

  useEffect(() => {
    if (!downloadUrl) return undefined;

    let cancelled = false;
    let objectUrl = null;

    const renderDocument = async () => {
      // Wait a tick for the DOM (docx container) to be ready.
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (cancelled) return;

      setIsLoading(true);
      setError(null);
      setIsRendered(false);
      setPdfUrl(null);

      try {
        const response = await fetch(downloadUrl);
        if (cancelled) return;
        if (!response.ok) {
          throw new Error(`Failed to download document: ${response.status}`);
        }
        const buffer = await response.arrayBuffer();
        if (cancelled) return;
        if (!buffer || buffer.byteLength === 0) {
          throw new Error("Document file is empty");
        }

        // Detect PDFs by declared type OR by the "%PDF" magic bytes — the files
        // bucket often serves them as octet-stream, so we can't trust fileType.
        const head = new Uint8Array(buffer.slice(0, 5));
        const looksPdf =
          head[0] === 0x25 &&
          head[1] === 0x50 &&
          head[2] === 0x44 &&
          head[3] === 0x46;

        if (isPdf || looksPdf) {
          objectUrl = URL.createObjectURL(
            new Blob([buffer], { type: "application/pdf" })
          );
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
            return;
          }
          setPdfUrl(objectUrl);
          setIsRendered(true);
          return;
        }

        if (!docContainerRef.current) {
          throw new Error("Document container was unmounted");
        }
        docContainerRef.current.innerHTML = "";
        await renderAsync(new Blob([buffer]), docContainerRef.current, null, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });

        if (!cancelled) {
          setIsRendered(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            `Failed to render document preview: ${
              err.message || "Unknown error"
            }`
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    renderDocument();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [downloadUrl, isPdf]);

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.grey[200],
          p: 4,
        }}
      >
        <Alert severity={"warning"} sx={{ maxWidth: 500 }}>
          <Typography variant={"body2"} sx={{ mb: 1 }}>
            {error}
          </Typography>
          <Typography variant={"caption"} color={"text.secondary"}>
            {
              "The document preview is not available, but you can still see the request details in the sidebar."
            }
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Show loading state only while fetching the download URL
  // Once we have the URL, keep the container mounted so docContainerRef stays available
  // during document rendering (the inline overlay at lines below handles the loading UX)
  if (!downloadUrl) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.grey[200],
        }}
      >
        <Typography color={"text.secondary"}>
          {"Loading document..."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        backgroundColor: theme.palette.grey[400],
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.9)",
            zIndex: 30,
          }}
        >
          <Typography color={"text.secondary"} fontWeight={500}>
            {"Rendering document..."}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          transform: `scale(${zoom})`,
          transformOrigin: "top center",
          transition: "transform 0.2s ease",
          "& .docx-wrapper": {
            background: "transparent !important",
            padding: "0 !important",
          },
          "& .docx": {
            boxShadow: "0 4px 20px rgba(0,0,0,0.15) !important",
            margin: "16px auto !important",
            backgroundColor: `${theme.brand.white} !important`,
          },
          "& section.docx": {
            marginBottom: "24px !important",
          },
        }}
      >
        {pdfUrl ? (
          <Box
            sx={{
              position: "relative",
              width: 650,
              minHeight: 900,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              backgroundColor: theme.brand.white,
              margin: "16px auto",
            }}
          >
            <embed
              src={pdfUrl}
              type={"application/pdf"}
              width={"100%"}
              height={"900px"}
              style={{ border: "none" }}
            />
            {placements.map((placement) => (
              <Box
                key={placement.id}
                sx={{
                  position: "absolute",
                  left: `${placement.x}%`,
                  top: `${placement.y}%`,
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <PreviewVariableMarker placement={placement} zoom={zoom} />
              </Box>
            ))}
            {annotations.map((annotation) => (
              <PreviewAnnotation
                key={annotation.id}
                annotation={annotation}
                zoom={zoom}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            <Box
              ref={docContainerRef}
              sx={{
                minWidth: 600,
                minHeight: isRendered ? "auto" : 800,
              }}
            />
            {isRendered && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
              >
                {placements.map((placement) => (
                  <Box
                    key={placement.id}
                    sx={{
                      position: "absolute",
                      left: `${placement.x}%`,
                      top: `${placement.y}%`,
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <PreviewVariableMarker placement={placement} zoom={zoom} />
                  </Box>
                ))}
                {annotations.map((annotation) => (
                  <PreviewAnnotation
                    key={annotation.id}
                    annotation={annotation}
                    zoom={zoom}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const PreviewSidebar = ({ request, impersonateCompanyId }) => {
  const theme = useTheme();
  const placements = request.placements || [];
  const annotations = request.annotations || [];
  const customFields = request.customFields || [];

  const handleDownloadDocument = () => {
    if (!request.s3FileId) return;
    saveFile(request.s3FileId, downloadFileName(request), impersonateCompanyId);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: "100%",
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
          {"Request Details"}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {/* Document Info */}
        <Box sx={{ mb: 3 }}>
          <Stack direction={"row"} alignItems={"center"} gap={1} sx={{ mb: 1 }}>
            <DocIcon fontSize={"small"} color={"primary"} />
            <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
              {"Document"}
            </Typography>
          </Stack>
          {request.s3FileId ? (
            <Typography
              variant={"body2"}
              color={"primary"}
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={handleDownloadDocument}
            >
              {request.templateName || "Uploaded Document"}
            </Typography>
          ) : (
            <Typography variant={"body2"} color={"text.secondary"}>
              {request.templateName || "Uploaded Document"}
            </Typography>
          )}
        </Box>

        {/* Placed Fields */}
        {placements.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={1}
              sx={{ mb: 1 }}
            >
              <PlaceIcon fontSize={"small"} color={"success"} />
              <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
                {"Fields Placed"} {"("}
                {placements.length}
                {")"}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              {placements.map((p) => {
                const typeColor = p.condition
                  ? "#0288d1"
                  : getTypeColor(p.variable.type);
                return (
                  <Tooltip
                    key={p.id}
                    title={
                      p.condition ? (
                        <Box>
                          <Typography
                            variant={"caption"}
                            sx={{ fontWeight: 600 }}
                          >
                            {"Show if:"}
                          </Typography>
                          <Typography
                            variant={"caption"}
                            display={"block"}
                            sx={{ fontFamily: "monospace" }}
                          >
                            {p.condition}
                          </Typography>
                        </Box>
                      ) : (
                        ""
                      )
                    }
                    placement={"left"}
                    arrow
                  >
                    <Chip
                      size={"small"}
                      label={
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          gap={0.5}
                        >
                          {getTypeIcon(p.variable.type)}
                          <span>
                            {p.variable.label}
                            {p.condition && " (conditional)"}
                          </span>
                        </Stack>
                      }
                      sx={{
                        justifyContent: "flex-start",
                        borderColor: typeColor,
                        color: typeColor,
                        fontStyle: p.condition ? "italic" : "normal",
                        "& .MuiChip-label": { pl: 0.5 },
                      }}
                      variant={"outlined"}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Custom Fields */}
        {customFields.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant={"caption"}
              sx={{
                fontWeight: 600,
                display: "block",
                mb: 1,
                color: "warning.main",
              }}
            >
              {"Custom Fields Requested"} {"("}
              {customFields.length}
              {")"}
            </Typography>
            <Stack spacing={1}>
              {customFields.map((field) => (
                <Paper
                  key={field.id}
                  variant={"outlined"}
                  sx={{
                    p: 1,
                    borderColor: "warning.main",
                    backgroundColor: "warning.lighter",
                  }}
                >
                  <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
                    {field.label}
                  </Typography>
                  {field.description && (
                    <Typography
                      variant={"caption"}
                      display={"block"}
                      color={"text.secondary"}
                      sx={{ mt: 0.5 }}
                    >
                      {field.description}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {/* Annotations */}
        {annotations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={1}
              sx={{ mb: 1 }}
            >
              <AnnotationIcon fontSize={"small"} color={"primary"} />
              <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
                {"Annotations"} {"("}
                {annotations.length}
                {")"}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              {annotations.map((a) => {
                const tool = ANNOTATION_TOOLS.find((t) => t.id === a.type);
                const Icon = tool?.icon;
                const fullLabel =
                  a.type === "comment" ? `"${a.text}"` : tool?.label;
                const truncatedLabel =
                  a.type === "comment"
                    ? `"${a.text?.substring(0, 15)}${
                        a.text?.length > 15 ? "..." : ""
                      }"`
                    : tool?.label;
                return (
                  <Tooltip
                    key={a.id}
                    title={fullLabel}
                    arrow
                    placement={"left"}
                  >
                    <Chip
                      size={"small"}
                      icon={Icon ? <Icon sx={{ fontSize: 14 }} /> : undefined}
                      label={truncatedLabel}
                      sx={{
                        justifyContent: "flex-start",
                        borderColor: tool?.color,
                        color: tool?.color,
                      }}
                      variant={"outlined"}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Request metadata */}
        <Stack spacing={1.5}>
          <Box>
            <Typography variant={"caption"} color={"text.secondary"}>
              {"Submitted By"}
            </Typography>
            <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
              {request.submittedBy || "Unknown"}
            </Typography>
          </Box>
          <Box>
            <Typography variant={"caption"} color={"text.secondary"}>
              {"Priority"}
            </Typography>
            <Chip
              size={"small"}
              label={request.priority || "MEDIUM"}
              color={
                request.priority === "HIGH"
                  ? "error"
                  : request.priority === "LOW"
                  ? "success"
                  : "warning"
              }
              variant={"outlined"}
              sx={{ ml: 1 }}
            />
          </Box>
          <RequestDescription request={request} />
        </Stack>
      </Box>
    </Paper>
  );
};

const RequestPreviewDialog = ({
  open,
  onClose,
  request,
  impersonateCompanyId,
}) => {
  const theme = useTheme();
  const [zoom, setZoom] = useState(0.75);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!request) return null;

  const handleZoomIn = () => setZoom((prev) => Math.min(1.5, prev + 0.1));
  const handleZoomOut = () => setZoom((prev) => Math.max(0.4, prev - 0.1));

  const handleDownload = async () => {
    if (!request.s3FileId) return;
    setIsDownloading(true);
    try {
      await saveFile(
        request.s3FileId,
        downloadFileName(request),
        impersonateCompanyId
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"xl"}
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5,
        }}
      >
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Typography variant={"h6"} sx={{ fontWeight: 600 }}>
            {"Request Preview: "}
            {request.id}
          </Typography>
          <Chip
            label={
              request.type === "NEW_TEMPLATE" ? "New Template" : "Template Edit"
            }
            size={"small"}
            color={request.type === "NEW_TEMPLATE" ? "primary" : "secondary"}
          />
        </Stack>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          {request.s3FileId && (
            <Tooltip title={"Download original file"}>
              <IconButton
                size={"small"}
                onClick={handleDownload}
                disabled={isDownloading}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          <Divider orientation={"vertical"} flexItem sx={{ mx: 0.5 }} />
          <Tooltip title={"Zoom out"}>
            <IconButton size={"small"} onClick={handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant={"caption"}
            sx={{ minWidth: 40, textAlign: "center" }}
          >
            {Math.round(zoom * 100)}
            {"%"}
          </Typography>
          <Tooltip title={"Zoom in"}>
            <IconButton size={"small"} onClick={handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation={"vertical"} flexItem sx={{ mx: 1 }} />
          <IconButton onClick={onClose} size={"small"}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
        <PreviewCanvas
          placements={request.placements || []}
          annotations={request.annotations || []}
          zoom={zoom}
          s3FileId={request.s3FileId}
          fileType={request.fileType}
          impersonateCompanyId={impersonateCompanyId}
        />
        <PreviewSidebar
          request={request}
          impersonateCompanyId={impersonateCompanyId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default RequestPreviewDialog;
